import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CloudLightning, Lock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /lab gate — checks admin JWT; if valid, hard-redirects to the static
 * AI Transformation Hub bundled under /public/lab/index.html.
 * If not, sends the user to /admin/login and returns them here.
 */
const LabGate = () => {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | denied | granted

  useEffect(() => {
    document.title = "AI Transformation Hub · Mia O'Neill";
    const token = typeof window !== "undefined" ? localStorage.getItem("admin_token") : null;
    if (!token) {
      setState("denied");
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setState("granted");
        // Small delay so the "entering..." card is visible for a beat
        setTimeout(() => {
          window.location.replace("/lab-app/index.html");
        }, 400);
      })
      .catch(() => {
        localStorage.removeItem("admin_token");
        setState("denied");
      });
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)] paper-grain flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="relative border border-black/15 bg-white/70 backdrop-blur-sm p-8 md:p-10">
          <span aria-hidden="true" className="absolute -top-px left-0 h-px w-20 bg-[#C2410C]" />

          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
              <CloudLightning size={20} />
            </div>
            <div>
              <div className="overline text-black/50">Private workspace</div>
              <div className="font-display text-2xl leading-tight">AI Transformation Hub</div>
            </div>
          </div>

          {state === "checking" && (
            <div data-testid="lab-checking" className="flex items-center gap-3 text-sm text-black/60">
              <span className="w-3 h-3 rounded-full bg-[#C2410C] animate-pulse" />
              Verifying access…
            </div>
          )}

          {state === "granted" && (
            <div data-testid="lab-granted" className="flex items-center gap-3 text-sm">
              <span className="w-3 h-3 rounded-full bg-emerald-600" />
              Access granted — entering the hub…
            </div>
          )}

          {state === "denied" && (
            <div data-testid="lab-denied">
              <div className="flex items-start gap-3 text-sm text-black/70 mb-6">
                <Lock size={16} className="text-[#C2410C] shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                  This workspace is private. Sign in to the admin panel to continue.
                </p>
              </div>
              <button
                onClick={() => navigate("/admin/login?next=/lab")}
                className="inline-flex items-center gap-2 bg-black text-[var(--bg)] px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#C2410C] transition-colors"
                data-testid="lab-signin"
              >
                Sign in <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabGate;
